'use client';

import type { CmsShellViewerMode } from '@/cms/chrome/shell-viewer-mode';
import { WorkspaceStudioSkeleton } from '@/cms/components/blocks/workspace/workspace-1/shell/workspace-studio-skeleton';
import { useWorkspaceSidebarExpansion } from '@/cms/components/blocks/workspace/workspace-1/shell/workspace-viewport';
import { scrollCmsWorkspaceRootWhenReady } from '@/cms/components/blocks/workspace/workspace-scroll';
import { subscribeCmsWorkspaceActivation } from '@/cms/components/blocks/workspace/workspace-session-controller';
import { CmsAccountSettingsDialogOwner } from '@/cms/components/chrome/cms-account-settings-dialog-owner';
import { CmsPublicThemeRoot } from '@/cms/components/chrome/cms-public-theme-root';
import {
  CMS_WORKSPACE_SIDEBAR_DEFAULT_EXPANDED,
  CmsWorkspaceSidebar,
} from '@/cms/components/chrome/cms-workspace-sidebar';
import type { CmsWorkspaceLaunchContext } from '@/cms/workspace/launch-context';
import { useIsMobile } from '@/hooks/ui/use-mobile';
import { cn } from '@/lib/ui/utils';
import type { ComponentProps, ReactNode } from 'react';
import { useEffect, useLayoutEffect, useState } from 'react';
import { useCmsWorkspaceAutoLaunch } from './use-cms-workspace-auto-launch';

type CmsWorkspaceSidebarModel = ComponentProps<
  typeof CmsWorkspaceSidebar
>['sidebar'];

const useBrowserLayoutEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect;
export function CmsPublicPageModeShell({
  announcement,
  canEnterWorkspaceMode,
  children,
  currentPath,
  footer,
  initialWorkspaceMode,
  locale,
  onSidebarNavigationAction,
  sidebar,
  standardHeader,
  suppressAuthenticatedAutoLaunch = false,
  viewerMode = 'live',
  workspaceContent,
  workspaceLaunchContext,
}: {
  announcement?: ReactNode;
  canEnterWorkspaceMode: boolean;
  children: ReactNode;
  currentPath: string;
  footer: ReactNode;
  initialWorkspaceMode: boolean;
  locale: string;
  onSidebarNavigationAction?: (actionKey: string) => void;
  sidebar: CmsWorkspaceSidebarModel;
  standardHeader: ReactNode;
  suppressAuthenticatedAutoLaunch?: boolean;
  viewerMode?: CmsShellViewerMode;
  workspaceContent?: ReactNode;
  workspaceLaunchContext?: CmsWorkspaceLaunchContext;
}) {
  const [isWorkspaceMode, setIsWorkspaceMode] = useState(initialWorkspaceMode);
  const {
    expanded: sidebarExpanded,
    setExpanded: setSidebarExpanded,
    viewportKind,
  } = useWorkspaceSidebarExpansion(CMS_WORKSPACE_SIDEBAR_DEFAULT_EXPANDED);
  const isStandardMobileViewport = useIsMobile();
  const authenticatedAutoLaunchSuppressed =
    suppressAuthenticatedAutoLaunch || viewerMode !== 'live';
  const hasAuthContinuationBootstrap = Boolean(
    !initialWorkspaceMode &&
      canEnterWorkspaceMode &&
      workspaceContent &&
      !authenticatedAutoLaunchSuppressed
  );
  useBrowserLayoutEffect(() => {
    if (initialWorkspaceMode || !canEnterWorkspaceMode) {
      return;
    }

    const activateWorkspaceMode = () => {
      setIsWorkspaceMode(true);
    };

    return subscribeCmsWorkspaceActivation(activateWorkspaceMode);
  }, [canEnterWorkspaceMode, initialWorkspaceMode]);

  useCmsWorkspaceAutoLaunch({
    canEnterWorkspaceMode,
    initialWorkspaceMode,
    isWorkspaceMode,
    suppressAuthenticatedAutoLaunch: authenticatedAutoLaunchSuppressed,
    workspaceContentAvailable: Boolean(workspaceContent),
    workspaceLaunchContext,
  });

  useEffect(() => {
    if (!isWorkspaceMode) {
      return;
    }

    scrollCmsWorkspaceRootWhenReady();
  }, [isWorkspaceMode]);

  const accountSettingsPresentation = (
    isWorkspaceMode
      ? viewportKind === 'mobile'
      : isStandardMobileViewport
  )
    ? 'drawer'
    : 'dialog';

  return (
    <CmsPublicThemeRoot mode={isWorkspaceMode ? 'workspace' : 'standard'}>
      {announcement}
      <CmsAccountSettingsDialogOwner
        locale={locale}
        presentation={accountSettingsPresentation}
        viewerMode={viewerMode}
      >
        <div
          data-workspace-auth-continuation-content={
            hasAuthContinuationBootstrap || undefined
          }
          className={cn(
            (isWorkspaceMode || hasAuthContinuationBootstrap) &&
              'cms-workspace-sidebar-shell',
            isWorkspaceMode ? 'flex min-h-screen w-full flex-col' : null
          )}
          data-workspace-sidebar-expanded={
            isWorkspaceMode || hasAuthContinuationBootstrap
              ? String(sidebarExpanded)
              : undefined
          }
          data-workspace-viewport={
            isWorkspaceMode || hasAuthContinuationBootstrap
              ? viewportKind
              : undefined
          }
        >
          <div data-workspace-sidebar-slot>
            {isWorkspaceMode || hasAuthContinuationBootstrap ? (
              <CmsWorkspaceSidebar
                sidebar={sidebar}
                currentPath={currentPath}
                expanded={sidebarExpanded}
                locale={locale}
                onNavigationAction={onSidebarNavigationAction}
                onExpandedChange={setSidebarExpanded}
                viewerMode={viewerMode}
              />
            ) : null}
          </div>

          <div
            className={cn(
              'cms-page-content-guard cms-workspace-sidebar-content min-w-0',
              isWorkspaceMode
                ? 'flex flex-1 flex-col pl-[var(--cms-workspace-sidebar-current-width)]'
                : null
            )}
          >
            <div data-workspace-standard-content hidden={isWorkspaceMode}>
              {standardHeader}
            </div>
            <main
              className={cn('flex-1', !isWorkspaceMode && 'pt-12 lg:pt-16')}
              style={
                isWorkspaceMode
                  ? { paddingTop: 'var(--cms-site-banner-offset, 0px)' }
                  : {
                      paddingTop:
                        'calc(4rem + var(--cms-site-banner-offset, 0px))',
                    }
              }
            >
              {workspaceContent ? (
                <>
                  <div data-workspace-standard-content hidden={isWorkspaceMode}>
                    {children}
                  </div>
                  <div
                    id="workspace"
                    data-workspace-content
                    hidden={!isWorkspaceMode && !hasAuthContinuationBootstrap}
                  >
                    {hasAuthContinuationBootstrap ? (
                      <div
                        aria-busy="true"
                        aria-hidden="true"
                        data-workspace-auth-continuation-bootstrap
                      >
                        <WorkspaceStudioSkeleton
                          markWorkspaceRoot={false}
                          rootId={null}
                        />
                      </div>
                    ) : null}
                    <div data-workspace-runtime-content>{workspaceContent}</div>
                  </div>
                </>
              ) : (
                children
              )}
            </main>
            {isWorkspaceMode ? null : (
              <div data-workspace-standard-content>{footer}</div>
            )}
          </div>
        </div>
      </CmsAccountSettingsDialogOwner>
    </CmsPublicThemeRoot>
  );
}
